import { isExternalURL } from "@dashboard/utils/urls";
import { sprinkles, Text } from "@saleor/macaw-ui-next";
import clsx from "clsx";
import type * as React from "react";
import { Link as RouterLink } from "react-router-dom";

interface LinkState {
  from?: string;
}

// Note: we need to skip the `dangerouslySetInnerHTML` prop from the `React.AnchorHTMLAttributes`
// in order to match react-router-dom Link props
interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "dangerouslySetInnerHTML"> {
  href?: string;
  color?: "primary" | "secondary";
  inline?: boolean;
  underline?: boolean;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
  state?: LinkState;
}

export const Link = (props: LinkProps): React.ReactNode => {
  const {
    className,
    children,
    inline = true,
    color = "primary",
    underline = false,
    onClick,
    disabled,
    href,
    target,
    rel,
    state,
    style,
    ...linkProps
  } = props;
  const opensNewTab = target === "_blank";

  const colorTheme = color === "primary" ? "accent1" : "default1";
  const textColor = disabled ? "default2" : colorTheme;

  const linkClassName = clsx(
    sprinkles({
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: "inherit",
      // Opt in with `underline`. Do not hover-underline by default: Link also
      // wraps buttons, command rows, and cards that already have their own hover.
      textDecoration: underline ? "underline" : "none",
      color: textColor,
    }),
    className,
  );

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    if (disabled) {
      return;
    }

    if (onClick) {
      // Only prevent default navigation for non-external links.
      if (href && !isExternalURL(href)) {
        event.preventDefault();
      }

      onClick(event);
    }
  };

  // Sprinkles doesn't support display: "inline", so we use inline styles for this.
  // currentColor keeps any underline on this node (never the UA link blue).
  const inlineStyle: React.CSSProperties = {
    display: inline ? "inline" : undefined,
    textDecorationColor: "currentColor",
    ...style,
  };

  const applySafeRelAttributes = opensNewTab && href && isExternalURL(href);

  const commonLinkProps = {
    className: linkClassName,
    onClick: handleClick,
    target,
    rel: rel ?? (applySafeRelAttributes ? "noopener noreferrer" : ""),
    style: inlineStyle,
    ...linkProps,
  } as const;

  if (href && !isExternalURL(href)) {
    const urlObject = new URL(href, window.location.origin);
    const routerLinkToParams = {
      pathname: urlObject.pathname,
      search: urlObject.search,
      hash: urlObject.hash,
      state,
    } as const;

    return (
      <RouterLink<LinkState>
        to={disabled ? "" : routerLinkToParams}
        {...commonLinkProps}
        aria-disabled={disabled}
      >
        {children}
      </RouterLink>
    );
  } else {
    return (
      <Text
        as="a"
        href={disabled ? undefined : href}
        display="block"
        color={textColor}
        {...commonLinkProps}
      >
        {children}
      </Text>
    );
  }
};

Link.displayName = "Link";
/**
 * @deprecated use named export
 */
export default Link;
